import type { AdminUser } from '@mollusk/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../api/endpoints';

const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '—');

/** Admin-only: add prospects (emails them a sign-in link), disable/enable, resend (#170). */
function UsersPage() {
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ['admin-users'], queryFn: api.adminUsers });
  const [form, setForm] = useState({ email: '', name: '', company: '', role: 'prospect' });
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const done = (text: string) => {
    setNotice({ ok: true, text });
    void qc.invalidateQueries({ queryKey: ['admin-users'] });
  };
  const fail = (err: unknown) =>
    setNotice({ ok: false, text: err instanceof Error ? err.message : 'failed' });

  const create = useMutation({
    mutationFn: () =>
      api.adminCreateUser({
        email: form.email,
        role: form.role as 'admin' | 'prospect',
        ...(form.name ? { name: form.name } : {}),
        ...(form.company ? { company: form.company } : {}),
      }),
    onSuccess: (u) => {
      setForm({ email: '', name: '', company: '', role: 'prospect' });
      done(`Added ${u.email} and emailed a sign-in link.`);
    },
    onError: fail,
  });
  const toggle = useMutation({
    mutationFn: (u: AdminUser) => api.adminUpdateUser(u.id, { disabled: !u.disabled }),
    onSuccess: (u) => done(`${u.email} ${u.disabled ? 'disabled' : 'enabled'}.`),
    onError: fail,
  });
  const resend = useMutation({
    mutationFn: (u: AdminUser) => api.adminResendInvite(u.id).then(() => u),
    onSuccess: (u) => done(`Sent a new sign-in link to ${u.email}.`),
    onError: fail,
  });

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Users
      </Typography>
      <Paper
        variant="outlined"
        component="form"
        sx={{ p: 2, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <TextField
          size="small"
          label="Email"
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <TextField
          size="small"
          label="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <TextField
          size="small"
          label="Company"
          value={form.company}
          onChange={(e) => setForm({ ...form, company: e.target.value })}
        />
        <TextField
          size="small"
          select
          label="Role"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="prospect">Prospect</MenuItem>
          <MenuItem value="admin">Admin</MenuItem>
        </TextField>
        <Button type="submit" variant="contained" disabled={create.isPending || !form.email}>
          Add and send link
        </Button>
      </Paper>
      {notice ? (
        <Alert severity={notice.ok ? 'success' : 'error'} sx={{ mb: 2 }}>
          {notice.text}
        </Alert>
      ) : null}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Terms accepted</TableCell>
              <TableCell>Last login</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(users.data?.users ?? []).map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.name ?? '—'}</TableCell>
                <TableCell>{u.company ?? '—'}</TableCell>
                <TableCell>{u.role}</TableCell>
                <TableCell>{day(u.terms_accepted_at)}</TableCell>
                <TableCell>{day(u.last_login_at)}</TableCell>
                <TableCell>{u.disabled ? 'Disabled' : 'Active'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Button size="small" onClick={() => resend.mutate(u)} disabled={u.disabled}>
                    Send link
                  </Button>
                  <Button
                    size="small"
                    color={u.disabled ? 'primary' : 'error'}
                    onClick={() => toggle.mutate(u)}
                  >
                    {u.disabled ? 'Enable' : 'Disable'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export const Route = createFileRoute('/admin/users')({ component: UsersPage });
